import React, { useState } from "react";
import "./ImageEditor.css";
import "../../styles/icons/icons.css";

export default function ImageEditor({ 
  images = [], 
  onClose, 
  onSave,
  onBack 
}) {
  const [selectedImageIndex] = useState(0);
  const [selectedTool, setSelectedTool] = useState(null);
  const [editedImages, setEditedImages] = useState([...images]);
  const [cropRect, setCropRect] = useState({ x: 0.1, y: 0.1, width: 0.8, height: 0.8 });
  const [draggingHandle, setDraggingHandle] = useState(null);
  const [dragStart, setDragStart] = useState(null);
  const [startRect, setStartRect] = useState(null);
  const [tags, setTags] = useState([]);
  const [currentTagInput, setCurrentTagInput] = useState("");
  const [textInput, setTextInput] = useState("");
  const [selectedFont, setSelectedFont] = useState("Arial");
  const [textColor, setTextColor] = useState("#000000");
  const [textSize, setTextSize] = useState(36);
  const [textBold, setTextBold] = useState(false);
  const [textItalic, setTextItalic] = useState(false);
  const [textAlignment, setTextAlignment] = useState('center');
  const [textBackground, setTextBackground] = useState('transparent');
  const [textShadow, setTextShadow] = useState(false);
  const [textOverlays, setTextOverlays] = useState([]);
  const [selectedTextId, setSelectedTextId] = useState(null);
  const [selectedTagId, setSelectedTagId] = useState(null);
  const [isDraggingText, setIsDraggingText] = useState(false);
  const [textDragStart, setTextDragStart] = useState(null);
  const [startTextPosition, setStartTextPosition] = useState(null);
  const [isResizingText, setIsResizingText] = useState(false);
  const [textResizeStart, setTextResizeStart] = useState(null);
  const [startTextSize, setStartTextSize] = useState(null);
  const [isDraggingTag, setIsDraggingTag] = useState(false);
  const [tagDragStart, setTagDragStart] = useState(null);
  const [startTagPosition, setStartTagPosition] = useState(null);
  const containerRef = React.useRef(null);
  const imageRef = React.useRef(null);

  const normalizeCropRect = (rect) => {
    const minWidth = 0.05;
    const minHeight = 0.05;
    
    // Clamp position to valid range
    let x = Math.max(0, Math.min(1 - minWidth, rect.x));
    let y = Math.max(0, Math.min(1 - minHeight, rect.y));
    
    // Clamp dimensions to valid range
    let width = Math.max(minWidth, rect.width);
    let height = Math.max(minHeight, rect.height);
    
    // Ensure right and bottom edges don't exceed 1
    if (x + width > 1) {
      width = 1 - x;
    }
    if (y + height > 1) {
      height = 1 - y;
    }
    
    return { x, y, width, height };
  };

  const rotateImageData = (dataUrl, degrees) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");
        const normalizedDegrees = ((degrees % 360) + 360) % 360;
        if (normalizedDegrees === 90 || normalizedDegrees === 270) {
          canvas.width = img.height;
          canvas.height = img.width;
        } else {
          canvas.width = img.width;
          canvas.height = img.height;
        }

        ctx.translate(canvas.width / 2, canvas.height / 2);
        ctx.rotate((normalizedDegrees * Math.PI) / 180);
        ctx.drawImage(img, -img.width / 2, -img.height / 2);
        resolve(canvas.toDataURL("image/png"));
      };
      img.onerror = reject;
      img.src = dataUrl;
    });
  };

  const handleRotate = async (degrees) => {
    const currentImage = editedImages[selectedImageIndex];
    if (!currentImage) return;
    try {
      const rotated = await rotateImageData(currentImage, degrees);
      setEditedImages((prev) => {
        const copy = [...prev];
        copy[selectedImageIndex] = rotated;
        return copy;
      });
    } catch (error) {
      console.error("Image rotate failed:", error);
    }
  };

  const updateTextOverlay = (id, changes) => {
    setTextOverlays((prev) => prev.map((item) => (item.id === id ? { ...item, ...changes } : item)));
  };

  const addOrUpdateTextOverlay = () => {
    const trimmed = textInput.trim();
    if (!trimmed) return;

    if (selectedTextId) {
      updateTextOverlay(selectedTextId, {
        text: trimmed,
        font: selectedFont,
        color: textColor,
        size: textSize,
        bold: textBold,
        italic: textItalic,
        alignment: textAlignment,
        background: textBackground,
        shadow: textShadow,
      });
      setSelectedTextId(null);
    } else {
      setTextOverlays((prev) => [
        ...prev,
        {
          id: Date.now(),
          text: trimmed,
          font: selectedFont,
          color: textColor,
          size: textSize,
          bold: textBold,
          italic: textItalic,
          alignment: textAlignment,
          background: textBackground,
          shadow: textShadow,
          x: 0.5,
          y: 0.15,
          width: 0.4, // Default width as fraction of image
        },
      ]);
    }

    setTextInput("");
  };

  const updateTagOverlay = (id, changes) => {
    setTags((prev) => prev.map((item) => (item.id === id ? { ...item, ...changes } : item)));
  };

  const beginTextDrag = (id, event) => {
    event.preventDefault();
    event.stopPropagation();
    const imageBounds = getImageBounds();
    if (!imageBounds) return;

    const overlay = textOverlays.find((item) => item.id === id);
    if (!overlay) return;

    setSelectedTextId(id);
    setIsDraggingText(true);
    setTextDragStart({ x: event.clientX, y: event.clientY, imageBounds });
    setStartTextPosition({ x: overlay.x, y: overlay.y });
    event.currentTarget.setPointerCapture(event.pointerId);
  };

  const beginTagDrag = (id, event) => {
    event.preventDefault();
    event.stopPropagation();
    const imageBounds = getImageBounds();
    if (!imageBounds) return;

    const overlay = tags.find((item) => item.id === id);
    if (!overlay) return;

    setSelectedTagId(id);
    setIsDraggingTag(true);
    setTagDragStart({ x: event.clientX, y: event.clientY, imageBounds });
    setStartTagPosition({ x: overlay.x, y: overlay.y });
    event.currentTarget.setPointerCapture(event.pointerId);
  };

  const handleTextPointerMove = (event) => {
    if (!isDraggingText || !textDragStart || !startTextPosition) return;
    const imageBounds = textDragStart.imageBounds;
    const dx = (event.clientX - textDragStart.x) / imageBounds.width;
    const dy = (event.clientY - textDragStart.y) / imageBounds.height;

    updateTextOverlay(selectedTextId, {
      x: Math.min(1, Math.max(0, startTextPosition.x + dx)),
      y: Math.min(1, Math.max(0, startTextPosition.y + dy)),
    });
  };

  const handleTagPointerMove = (event) => {
    if (!isDraggingTag || !tagDragStart || !startTagPosition) return;
    const imageBounds = tagDragStart.imageBounds;
    const dx = (event.clientX - tagDragStart.x) / imageBounds.width;
    const dy = (event.clientY - tagDragStart.y) / imageBounds.height;

    updateTagOverlay(selectedTagId, {
      x: Math.min(1, Math.max(0, startTagPosition.x + dx)),
      y: Math.min(1, Math.max(0, startTagPosition.y + dy)),
    });
  };

  const endTextDrag = (event) => {
    if (event.currentTarget && event.pointerId) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    setIsDraggingText(false);
    setTextDragStart(null);
    setStartTextPosition(null);
  };

  const endTagDrag = (event) => {
    if (event.currentTarget && event.pointerId) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    setIsDraggingTag(false);
    setTagDragStart(null);
    setStartTagPosition(null);
  };

  const beginTextResize = (id, event) => {
    event.preventDefault();
    event.stopPropagation();
    const imageBounds = getImageBounds();
    if (!imageBounds) return;

    const overlay = textOverlays.find((item) => item.id === id);
    if (!overlay) return;

    setSelectedTextId(id);
    setIsResizingText(true);
    setTextResizeStart({ x: event.clientX, y: event.clientY, imageBounds });
    setStartTextSize(overlay.size);
    event.currentTarget.setPointerCapture(event.pointerId);
  };

  const handleTextResizeMove = (event) => {
    if (!isResizingText || !textResizeStart || startTextSize === null) return;
    const dx = event.clientX - textResizeStart.x;
    const dy = event.clientY - textResizeStart.y;
    const scale = Math.max(0.5, Math.min(3, startTextSize + (dx + dy) * 0.5));

    updateTextOverlay(selectedTextId, {
      size: Math.round(scale),
    });
  };

  const endTextResize = (event) => {
    if (event.currentTarget && event.pointerId) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    setIsResizingText(false);
    setTextResizeStart(null);
    setStartTextSize(null);
  };

  const cropImageData = (dataUrl, rect) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement("canvas");
        const sx = rect.x * img.naturalWidth;
        const sy = rect.y * img.naturalHeight;
        const sw = rect.width * img.naturalWidth;
        const sh = rect.height * img.naturalHeight;
        canvas.width = sw;
        canvas.height = sh;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, sx, sy, sw, sh, 0, 0, sw, sh);
        resolve(canvas.toDataURL("image/png"));
      };
      img.onerror = reject;
      img.src = dataUrl;
    });
  };

  const handleCropApply = async () => {
    const currentImage = editedImages[selectedImageIndex];
    if (!currentImage) return;
    try {
      const composed = await renderImageWithOverlays(currentImage, tags, textOverlays);
      const cropped = await cropImageData(composed, cropRect);
      setEditedImages((prev) => {
        const copy = [...prev];
        copy[selectedImageIndex] = cropped;
        return copy;
      });

      setTextOverlays((prev) =>
        prev.map((item) => ({
          ...item,
          x: Math.min(1, Math.max(0, (item.x - cropRect.x) / cropRect.width)),
          y: Math.min(1, Math.max(0, (item.y - cropRect.y) / cropRect.height)),
        }))
      );
      setTags((prev) =>
        prev.map((item) => ({
          ...item,
          x: Math.min(1, Math.max(0, (item.x - cropRect.x) / cropRect.width)),
          y: Math.min(1, Math.max(0, (item.y - cropRect.y) / cropRect.height)),
        }))
      );

      setSelectedTool(null);
    } catch (error) {
      console.error("Image crop failed:", error);
    }
  };

  const getImageBounds = () => {
    if (!imageRef.current) return null;
    return imageRef.current.getBoundingClientRect();
  };

  const getContainerBounds = () => {
    if (!containerRef.current) return null;
    return containerRef.current.getBoundingClientRect();
  };

  const beginCropDrag = (handle, event) => {
    event.preventDefault();
    const imageBounds = getImageBounds();
    const containerBounds = getContainerBounds();
    if (!imageBounds || !containerBounds) return;
    
    setDraggingHandle(handle);
    setDragStart({ 
      x: event.clientX, 
      y: event.clientY,
      imageBounds,
      containerBounds
    });
    setStartRect(cropRect);
    event.currentTarget.setPointerCapture(event.pointerId);
  };

  const handleCropPointerMove = (event) => {
    if (!draggingHandle || !dragStart || !startRect) return;
    
    const imageBounds = dragStart.imageBounds;
    const containerBounds = dragStart.containerBounds;
    if (!imageBounds || !containerBounds) return;

    // Calculate relative movement based on image dimensions
    const dx = (event.clientX - dragStart.x) / imageBounds.width;
    const dy = (event.clientY - dragStart.y) / imageBounds.height;
    
    let updatedRect = { ...startRect };

    switch (draggingHandle) {
      case 'move':
        updatedRect.x = startRect.x + dx;
        updatedRect.y = startRect.y + dy;
        break;
      case 'nw':
        updatedRect.x = startRect.x + dx;
        updatedRect.y = startRect.y + dy;
        updatedRect.width = startRect.width - dx;
        updatedRect.height = startRect.height - dy;
        break;
      case 'ne':
        updatedRect.y = startRect.y + dy;
        updatedRect.width = startRect.width + dx;
        updatedRect.height = startRect.height - dy;
        break;
      case 'sw':
        updatedRect.x = startRect.x + dx;
        updatedRect.width = startRect.width - dx;
        updatedRect.height = startRect.height + dy;
        break;
      case 'se':
        updatedRect.width = startRect.width + dx;
        updatedRect.height = startRect.height + dy;
        break;
      default:
        return;
    }

    const normalized = normalizeCropRect(updatedRect);
    setCropRect(normalized);
  };

  const endCropDrag = (event) => {
    if (event.currentTarget && event.pointerId) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    setDraggingHandle(null);
    setDragStart(null);
    setStartRect(null);
  };

  const handleCropReset = () => {
    setEditedImages((prev) => {
      const copy = [...prev];
      copy[selectedImageIndex] = images[selectedImageIndex];
      return copy;
    });
    setCropRect({ x: 0.1, y: 0.1, width: 0.8, height: 0.8 });
  };

  const renderImageWithOverlays = (dataUrl, overlayTags, overlaysText) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement("canvas");
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0);

        if (overlayTags.length) {
          overlayTags.forEach((tag) => {
            const text = `#${tag.text}`;
            const x = tag.x * canvas.width;
            const y = tag.y * canvas.height;
            ctx.font = "bold 28px Arial";
            const padding = 12;
            const width = ctx.measureText(text).width + padding * 2;
            const height = 44;
            ctx.fillStyle = "rgba(24, 119, 242, 0.9)";
            ctx.fillRect(x - 10, y - 32, width, height);
            ctx.fillStyle = "white";
            ctx.fillText(text, x, y);
          });
        }

        if (overlaysText.length) {
          overlaysText.forEach((item) => {
            // Build font string with all properties
            let fontWeight = item.bold ? 'bold' : 'normal';
            let fontStyle = item.italic ? 'italic' : 'normal';
            let fontSize = item.size || 36;
            ctx.font = `${fontStyle} ${fontWeight} ${fontSize}px ${item.font}`;
            ctx.fillStyle = item.color;
            ctx.textAlign = item.alignment || 'center';
            ctx.textBaseline = 'middle';

            const x = item.x * canvas.width;
            const y = item.y * canvas.height;

            // Handle background
            if (item.background && item.background !== 'transparent') {
              const textWidth = ctx.measureText(item.text).width;
              const textHeight = fontSize * 1.2;
              const padding = 8;
              ctx.fillStyle = item.background;
              ctx.fillRect(
                x - textWidth / 2 - padding,
                y - textHeight / 2 - padding,
                textWidth + padding * 2,
                textHeight + padding * 2
              );
            }

            // Handle shadow
            if (item.shadow) {
              ctx.shadowColor = 'rgba(0, 0, 0, 0.5)';
              ctx.shadowBlur = 4;
              ctx.shadowOffsetX = 2;
              ctx.shadowOffsetY = 2;
            } else {
              ctx.shadowColor = 'transparent';
              ctx.shadowBlur = 0;
              ctx.shadowOffsetX = 0;
              ctx.shadowOffsetY = 0;
            }

            // Draw text (handle multi-line)
            const lines = item.text.split('\n');
            const lineHeight = fontSize * 1.2;
            let currentY = y - (lines.length - 1) * lineHeight / 2;

            lines.forEach((line, index) => {
              // Handle background for each line if needed
              if (item.background && item.background !== 'transparent') {
                const textWidth = ctx.measureText(line).width;
                const textHeight = fontSize * 1.2;
                const padding = 8;
                ctx.fillStyle = item.background;
                ctx.fillRect(
                  x - textWidth / 2 - padding,
                  currentY - textHeight / 2 - padding,
                  textWidth + padding * 2,
                  textHeight + padding * 2
                );
              }

              // Draw the line
              ctx.fillStyle = item.color;
              ctx.fillText(line, x, currentY);
              currentY += lineHeight;
            });
          });
        }

        resolve(canvas.toDataURL("image/png"));
      };
      img.onerror = reject;
      img.src = dataUrl;
    });
  };

  const handleSave = async () => {
    const currentImage = editedImages[selectedImageIndex];
    if (!currentImage) {
      onSave(editedImages);
      onClose();
      return;
    }

    try {
      const merged = await renderImageWithOverlays(currentImage, tags, textOverlays);
      const finalImages = [...editedImages];
      finalImages[selectedImageIndex] = merged;
      onSave(finalImages);
    } catch (error) {
      console.error("Failed to compose overlays into image:", error);
      onSave(editedImages);
    }
    onClose();
  };

  const handleCancel = () => {
    onClose();
  };

  const handleBack = () => {
    if (onBack) {
      onBack();
    } else {
      onClose();
    }
  };

  const handleToolSelect = (tool) => {
    setSelectedTool(tool);
    // Here you would implement the specific tool functionality
    console.log(`Selected tool: ${tool}`);
  };

  const renderToolContent = () => {
    switch (selectedTool) {
      case 'crop':
        return (
          <div className="tool-content">
            <h3>Crop Image</h3>
            <p>Drag the crop box or handles to adjust the selection.</p>
            <div className="crop-actions">
              <button className="crop-action-btn" onClick={handleCropApply}>
                Apply crop
              </button>
              <button className="crop-action-btn secondary" onClick={handleCropReset}>
                Reset
              </button>
            </div>
          </div>
        );

      case 'tag':
        return (
          <div className="tool-content">
            <h3>Tag Photo</h3>
            <input 
              type="text" 
              placeholder="Enter tag name..."
              className="tag-input"
              value={currentTagInput}
              onChange={(e) => setCurrentTagInput(e.target.value)}
            />
            <button
              className="add-tag-btn"
              onClick={() => {
                if (!currentTagInput.trim()) return;
                setTags((prev) => [
                  ...prev,
                  {
                    id: Date.now(),
                    text: currentTagInput.trim(),
                    x: 0.15,
                    y: 0.15,
                  },
                ]);
                setCurrentTagInput("");
              }}
            >
              Add Tag
            </button>
            {tags.length > 0 && (
              <div className="tag-list">
                {tags.map((tag) => (
                  <span key={tag.id} className="tag-chip">#{tag.text}</span>
                ))}
              </div>
            )}
          </div>
        );
      case 'text':
        return (
          <div className="tool-content">
            <h3>Add Text</h3>
            <textarea
              placeholder="Enter text..."
              className="text-input"
              value={textInput}
              onChange={(e) => setTextInput(e.target.value)}
              rows="2"
            />

            {/* Font and Size */}
            <div className="text-options-row">
              <select
                className="font-select"
                value={selectedFont}
                onChange={(e) => setSelectedFont(e.target.value)}
              >
                <option value="Arial">Arial</option>
                <option value="Helvetica">Helvetica</option>
                <option value="Georgia">Georgia</option>
                <option value="Times New Roman">Times New Roman</option>
                <option value="Impact">Impact</option>
                <option value="Courier New">Courier New</option>
                <option value="Lucida Sans Unicode">Lucida Sans</option>
                <option value="Palatino Linotype">Palatino</option>
                <option value="Comic Sans MS">Comic Sans</option>
                <option value="Trebuchet MS">Trebuchet</option>
              </select>
              <div className="size-control">
                <span className="size-label">Size:</span>
                <input
                  type="range"
                  min="12"
                  max="120"
                  value={textSize}
                  onChange={(e) => setTextSize(parseInt(e.target.value))}
                  className="size-slider"
                />
                <span className="size-value">{textSize}px</span>
              </div>
            </div>

            {/* Style Controls */}
            <div className="text-style-controls">
              <button
                className={`style-btn ${textBold ? 'active' : ''}`}
                onClick={() => setTextBold(!textBold)}
                title="Bold"
              >
                <strong>B</strong>
              </button>
              <button
                className={`style-btn ${textItalic ? 'active' : ''}`}
                onClick={() => setTextItalic(!textItalic)}
                title="Italic"
              >
                <em>I</em>
              </button>
              <button
                className={`style-btn ${textShadow ? 'active' : ''}`}
                onClick={() => setTextShadow(!textShadow)}
                title="Shadow"
              >
                <span style={{textShadow: '1px 1px 2px rgba(0,0,0,0.5)'}}>S</span>
              </button>
            </div>

            {/* Color and Background */}
            <div className="text-options-row">
              <div className="color-control">
                <label>Text Color:</label>
                <input
                  type="color"
                  value={textColor}
                  onChange={(e) => setTextColor(e.target.value)}
                  className="color-picker"
                />
              </div>
              <div className="color-control">
                <label>Background:</label>
                <select
                  value={textBackground}
                  onChange={(e) => setTextBackground(e.target.value)}
                  className="bg-select"
                >
                  <option value="transparent">None</option>
                  <option value="rgba(255,255,255,0.8)">White</option>
                  <option value="rgba(0,0,0,0.7)">Black</option>
                  <option value="rgba(255,0,0,0.7)">Red</option>
                  <option value="rgba(0,255,0,0.7)">Green</option>
                  <option value="rgba(0,0,255,0.7)">Blue</option>
                  <option value="rgba(255,255,0,0.7)">Yellow</option>
                </select>
              </div>
            </div>

            {/* Alignment */}
            {/* <div className="alignment-controls">
              <span className="align-label">Align:</span>
              <button
                className={`align-btn ${textAlignment === 'left' ? 'active' : ''}`}
                onClick={() => setTextAlignment('left')}
                title="Left align"
              >
                ⬅
              </button>
              <button
                className={`align-btn ${textAlignment === 'center' ? 'active' : ''}`}
                onClick={() => setTextAlignment('center')}
                title="Center align"
              >
                ⬌
              </button>
              <button
                className={`align-btn ${textAlignment === 'right' ? 'active' : ''}`}
                onClick={() => setTextAlignment('right')}
                title="Right align"
              >
                ➡
              </button>
            </div> */}

            <button
              className="add-text-btn"
              onClick={addOrUpdateTextOverlay}
            >
              {selectedTextId ? "Update Text" : "Add Text"}
            </button>

            {textOverlays.length > 0 && (
              <div className="text-layers">
                <h4>Text Layers:</h4>
                {textOverlays.map((item, index) => (
                  <div key={item.id} className="text-layer-item">
                    <span className="layer-text" title={item.text}>
                      {item.text.length > 20 ? item.text.substring(0, 20) + '...' : item.text}
                    </span>
                    <div className="layer-actions">
                      <button
                        className="layer-edit-btn"
                        onClick={() => {
                          setSelectedTextId(item.id);
                          setTextInput(item.text);
                          setSelectedFont(item.font);
                          setTextColor(item.color);
                          setTextSize(item.size || 36);
                          setTextBold(item.bold || false);
                          setTextItalic(item.italic || false);
                          setTextAlignment(item.alignment || 'center');
                          setTextBackground(item.background || 'transparent');
                          setTextShadow(item.shadow || false);
                        }}
                        title="Edit"
                      >
                        ✏
                      </button>
                      <button
                        className="layer-delete-btn"
                        onClick={() => {
                          setTextOverlays(prev => prev.filter(t => t.id !== item.id));
                          if (selectedTextId === item.id) {
                            setSelectedTextId(null);
                          }
                        }}
                        title="Delete"
                      >
                        🗑
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      case 'alt':
        return (
          <div className="tool-content">
            <h3>Alternative Text</h3>
            <textarea 
              placeholder="Describe this image for accessibility..."
              className="alt-text-input"
              rows="4"
            />
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="image-editor-overlay">
      <div className="image-editor-container">
        {/* Header */}
        <div className="editor-header">
          <button className="back-btn" onClick={handleBack}>
            <i className="back-icon">←</i>
          </button>
          <h2 className="editor-title">Photo detail</h2>
        </div>

        <div className="editor-content">
          {/* Left Sidebar - Tools */}
          <div className="editor-sidebar">
            <div className="tool-options">
              <button 
                className={`tool-option ${selectedTool === 'crop' ? 'selected' : ''}`}
                onClick={() => handleToolSelect('crop')}
              >
                <i className="crop_icon"></i>
                <span>Crop</span>
              </button>
              
              <button 
                className="tool-option"
                onClick={() => handleRotate(90)}
              >
                <i className="refresh_icon"></i>
                <span>Rotate</span>
              </button>
              
              <button 
                className={`tool-option ${selectedTool === 'tag' ? 'selected' : ''}`}
                onClick={() => handleToolSelect('tag')}
              >
                <i className="tag_icon"></i>
                <span>Tag photo</span>
              </button>
              
              <button 
                className={`tool-option ${selectedTool === 'text' ? 'selected' : ''}`}
                onClick={() => handleToolSelect('text')}
              >
                <i className="edit_icon"></i>
                <span>Text tool</span>
              </button>
              
              {/* <button 
                className={`tool-option ${selectedTool === 'alt' ? 'selected' : ''}`}
                onClick={() => handleToolSelect('alt')}
              >
                <i className="edit_icon"></i>
                <span>Alternative text</span>
              </button> */}
            </div>

            {/* Action Buttons */}
            <div className="action-buttons">
              <button 
                className="save-btn"
                onClick={handleSave}
              >
                Save
              </button>
              <button 
                className="cancel-btn"
                onClick={handleCancel}
              >
                Cancel
              </button>
            </div>
          </div>

          {/* Main Content Area */}
          <div className="editor-main">
            {/* Image Display */}
            <div className="image-display" ref={containerRef}>
              {editedImages.length > 0 && (
                <>
                  <img 
                    src={editedImages[selectedImageIndex]} 
                    alt="Editing"
                    className="editing-image"
                    ref={imageRef}
                  />
                  {tags.length > 0 && (
                    <>
                      {tags.map((tag) => (
                        <div
                          key={tag.id}
                          className={`image-tag-overlay ${selectedTagId === tag.id ? 'selected-tag' : ''}`}
                          style={{
                            left: `${tag.x * 100}%`,
                            top: `${tag.y * 100}%`,
                          }}
                          onPointerDown={(event) => beginTagDrag(tag.id, event)}
                          onPointerMove={handleTagPointerMove}
                          onPointerUp={endTagDrag}
                          onPointerCancel={endTagDrag}
                          onClick={() => setSelectedTagId(tag.id)}
                        >
                          #{tag.text}
                        </div>
                      ))}
                    </>
                  )}
                  {textOverlays.map((item) => (
                    <div
                      key={item.id}
                      className={`image-text-overlay ${selectedTextId === item.id ? 'selected-text' : ''}`}
                      style={{
                        left: `${item.x * 100}%`,
                        top: `${item.y * 100}%`,
                        color: item.color,
                        fontFamily: item.font,
                        fontSize: `${item.size || 36}px`,
                        fontWeight: item.bold ? 'bold' : 'normal',
                        fontStyle: item.italic ? 'italic' : 'normal',
                        textAlign: item.alignment || 'center',
                        textShadow: item.shadow ? '2px 2px 4px rgba(0,0,0,0.5)' : 'none',
                        backgroundColor: item.background && item.background !== 'transparent' ? item.background : 'transparent',
                        padding: item.background && item.background !== 'transparent' ? '4px 8px' : '0',
                        borderRadius: item.background && item.background !== 'transparent' ? '4px' : '0',
                        minWidth: '20px',
                        minHeight: '20px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: item.alignment === 'left' ? 'flex-start' : item.alignment === 'right' ? 'flex-end' : 'center',
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedTool('text');
                        setSelectedTextId(item.id);
                        setTextInput(item.text);
                        setSelectedFont(item.font);
                        setTextColor(item.color);
                        setTextSize(item.size || 36);
                        setTextBold(item.bold || false);
                        setTextItalic(item.italic || false);
                        setTextAlignment(item.alignment || 'center');
                        setTextBackground(item.background || 'transparent');
                        setTextShadow(item.shadow || false);
                      }}
                      onPointerDown={(event) => beginTextDrag(item.id, event)}
                      onPointerMove={handleTextPointerMove}
                      onPointerUp={endTextDrag}
                      onPointerCancel={endTextDrag}
                    >
                      {item.text}
                      {selectedTextId === item.id && (
                        <div
                          className="text-resize-handle"
                          onPointerDown={(event) => {
                            event.stopPropagation();
                            beginTextResize(item.id, event);
                          }}
                          onPointerMove={handleTextResizeMove}
                          onPointerUp={endTextResize}
                          onPointerCancel={endTextResize}
                        />
                      )}
                    </div>
                  ))}
                  {selectedTool === 'text' && textInput.trim() && (
                    <div
                      className="image-text-overlay image-text-preview"
                      style={{
                        left: `50%`,
                        top: `85%`,
                        color: textColor,
                        fontFamily: selectedFont,
                        fontSize: `${textSize}px`,
                        fontWeight: textBold ? 'bold' : 'normal',
                        fontStyle: textItalic ? 'italic' : 'normal',
                        textAlign: textAlignment,
                        textShadow: textShadow ? '2px 2px 4px rgba(0,0,0,0.5)' : 'none',
                        backgroundColor: textBackground && textBackground !== 'transparent' ? textBackground : 'transparent',
                        padding: textBackground && textBackground !== 'transparent' ? '4px 8px' : '0',
                        borderRadius: textBackground && textBackground !== 'transparent' ? '4px' : '0',
                      }}
                    >
                      {textInput.trim()}
                    </div>
                  )}
                  {selectedTool === 'crop' && (
                    <div
                      className="crop-overlay"
                      onPointerMove={handleCropPointerMove}
                      onPointerUp={endCropDrag}
                      onPointerCancel={endCropDrag}
                    >
                      <div
                        className="crop-area"
                        style={{
                          left: `${cropRect.x * 100}%`,
                          top: `${cropRect.y * 100}%`,
                          width: `${cropRect.width * 100}%`,
                          height: `${cropRect.height * 100}%`
                        }}
                        onPointerDown={(event) => {
                          if (event.target === event.currentTarget) {
                            beginCropDrag('move', event);
                          }
                        }}
                      >
                        <div
                          className="crop-handle crop-handle-nw"
                          onPointerDown={(event) => beginCropDrag('nw', event)}
                        />
                        <div
                          className="crop-handle crop-handle-ne"
                          onPointerDown={(event) => beginCropDrag('ne', event)}
                        />
                        <div
                          className="crop-handle crop-handle-sw"
                          onPointerDown={(event) => beginCropDrag('sw', event)}
                        />
                        <div
                          className="crop-handle crop-handle-se"
                          onPointerDown={(event) => beginCropDrag('se', event)}
                        />
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Tool Content */}
            {selectedTool && (
              <div className="tool-panel">
                {renderToolContent()}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
